<template>
    <div class="sap-address-config">
        <el-card class="config-card">
            <div slot="header">
                <span class="card-title">SAP接口调用开关</span>
                <el-tag v-if="callAvailable" size="small" type="success">正常调用</el-tag>
                <el-tag v-else size="small" type="danger">已停用, 调用被拦截</el-tag>
            </div>
            <el-form label-width="120px">
                <el-form-item label="允许SAP调用">
                    <el-switch v-model="callAvailable" active-text="启用" inactive-text="已停用"
                               :disabled="callSwitching" @change="onCallAvailableChange"/>
                    <div class="tip-text">
                        开关存于系统参数, 切换后即时生效, 无需重启服务: 停用后系统内的SAP接口调用立即被拦截并提示已停用, 恢复后立即放行。
                        <span class="danger-text">本开关不含工时回写(CNF/PCNF)</span>, 工时回写的关闭时段仍在下方按日历维护。
                    </div>
                </el-form-item>
            </el-form>
        </el-card>

        <el-card class="config-card">
            <div slot="header">
                <span class="card-title">工时回写(CNF/PCNF)关闭时段</span>
                <el-tag v-if="writeBackCalendarClosedNow" size="small" type="warning">关闭时段生效中, 回写延迟执行</el-tag>
                <el-tag v-else size="small" type="success">正常回写</el-tag>
            </div>
            <div class="tip-text d-flex">
                工时回写的关闭由日历管理中类型为"SAP关闭"的条目时间范围控制:
                处于时间范围内时, 下工/工序完工产生的回写不会立即执行, 而是延迟到时段结束后自动补回, 与上方调用开关互不影响
                <el-link class="ml-auto" type="primary" @click="$router.push('/sithMes/mst/sithMesMstCalendar/list')">前往日历管理维护</el-link>
            </div>
        </el-card>

        <el-card class="config-card address-card">
            <div slot="header">
                <span class="card-title">SAP连接地址热切换配置</span>
                <el-tag v-if="!configs.length" size="small" type="info">未配置, 使用默认地址</el-tag>
                <el-tag v-else-if="activeRowIndex >= 0" size="small" type="success">第{{ activeRowIndex + 1 }}条生效中</el-tag>
                <el-tag v-else size="small" type="warning">均未到生效时间, 使用默认地址</el-tag>
            </div>
            <el-alert v-if="parseError" type="error" :closable="false" show-icon class="parse-error-alert"
                      title="系统参数中的配置JSON无法解析, 当前按默认地址调用; 保存将覆盖为下方列表内容"/>
            <el-table :data="configs" size="small" border>
                <el-table-column type="index" label="#" width="45"/>
                <el-table-column label="应用服务器地址">
                    <template slot-scope="scope">
                        <el-input v-model="scope.row.ashost" :disabled="isLocked(scope.row)"
                                  :placeholder="'默认: ' + (defaultConfig.ashost || '-')" clearable/>
                    </template>
                </el-table-column>
                <el-table-column label="系统编号(sysnr)" width="150">
                    <template slot-scope="scope">
                        <el-input v-model="scope.row.sysnr" :disabled="isLocked(scope.row)"
                                  :placeholder="'默认: ' + (defaultConfig.sysnr || '-')" clearable/>
                    </template>
                </el-table-column>
                <el-table-column label="Client号" width="130">
                    <template slot-scope="scope">
                        <el-input v-model="scope.row.client" :disabled="isLocked(scope.row)"
                                  :placeholder="'默认: ' + (defaultConfig.client || '-')" clearable/>
                    </template>
                </el-table-column>
                <el-table-column label="配置生效时间从" width="220">
                    <template slot-scope="scope">
                        <el-date-picker v-model="scope.row.effectiveFrom" type="datetime"
                                        value-format="yyyy-MM-dd HH:mm:ss" :disabled="isLocked(scope.row)"
                                        placeholder="留空立即生效" style="width: 100%"/>
                    </template>
                </el-table-column>
                <el-table-column label="状态" width="90">
                    <template slot-scope="scope">
                        <el-tag v-if="scope.row.active" size="mini" type="success">生效中</el-tag>
                        <el-tag v-else-if="isFuture(scope.row)" size="mini" type="warning">待生效</el-tag>
                        <el-tag v-else-if="scope.row.saved" size="mini" type="info">已覆盖</el-tag>
                        <el-tag v-else size="mini">新增</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="150">
                    <template slot-scope="scope">
                        <b-button size="sm" variant="link" class="cell-link" :disabled="scope.row.testing"
                                  @click="testConnection(scope.row)">
                            <b-spinner v-if="scope.row.testing" small class="mr-1"/>测试连接
                        </b-button>
                        <b-button size="sm" variant="link" class="cell-link danger-text" :disabled="scope.row.active"
                                  @click="configs.splice(scope.$index, 1)">删除</b-button>
                    </template>
                </el-table-column>
            </el-table>
            <div class="table-actions">
                <b-button size="sm" variant="outline-primary" @click="addRow">
                    <i class="fa fa-plus mr-1"/>添加配置
                </b-button>
            </div>
            <el-form label-width="140px">
                <el-form-item label="配置JSON">
                    <el-input :value="formJsonPreview" type="textarea" :rows="2" readonly/>
                    <div class="tip-text">
                        保存到系统参数 sithMes.sap.address.config; 生效规则: 已到生效时间的条目中取生效时间最新的一条,
                        生效时间留空表示立即生效, 地址字段留空的沿用默认值;
                        到达生效时间后, 下一次SAP调用自动切换到该条地址, 无需重启;
                        已生效的条目(生效中/已覆盖)锁定不可修改, 生效中的条目不可删除, 如需变更地址请新增一条配置
                    </div>
                </el-form-item>
                <el-form-item>
                    <b-button variant="primary" :disabled="saving" @click="saveConfig">
                        <b-spinner v-if="saving" small class="mr-1"/>保存配置
                    </b-button>
                    <b-button variant="danger" class="ml-2" :disabled="!configJson || activeRowIndex >= 0"
                              @click="clearConfig">清除全部</b-button>
                    <b-button variant="outline-secondary" class="ml-2" :disabled="loading" @click="refresh">
                        <b-spinner v-if="loading" small class="mr-1"/>
                        <i v-else class="fa fa-sync mr-1"/>刷新(放弃改动)
                    </b-button>
                </el-form-item>
            </el-form>
        </el-card>

        <el-card class="config-card drawing-test-card">
            <div slot="header">
                <span class="card-title">调用测试(图纸清单)</span>
                <el-tag size="small" type="info">仅取清单, 不下载图纸</el-tag>
            </div>
            <div class="d-flex align-items-center">
                <el-input v-model="testMaterialCode" placeholder="输入物料号" clearable style="width: 280px"
                          @keyup.enter.native="testDrawingList"/>
                <b-button variant="primary" size="sm" class="ml-2" :disabled="drawingTesting || !testMaterialCode.trim()"
                          @click="testDrawingList">
                    <b-spinner v-if="drawingTesting" small class="mr-1"/>
                    <i v-else class="fa fa-fw fa-paper-plane mr-1"/>调用测试
                </b-button>
            </div>
            <div class="tip-text">
                按物料号调用与图纸下载相同的SAP接口(/SIE/CHN_DOCUMENT_LINK)取图纸清单, 只回显清单, 不下载任何图纸文件;
                该调用不受SAP接口调用开关拦截, 停用期间也可用它验证当前生效的地址是否真的可用。
                验证范围是"本服务→SAP连接服务→SAP + 当前生效地址", 不覆盖图纸下载所在服务自身的连通性与地址缓存
            </div>

            <template v-if="drawingResult">
                <el-alert v-if="!drawingResult.success" type="error" :closable="false" show-icon class="result-alert"
                          :title="'调用失败: ' + (drawingResult.message || '未知错误')"/>
                <template v-else>
                    <div class="result-summary">
                        物料 {{ drawingResult.materialCode }}: SAP返回 {{ drawingResult.total }} 条,
                        其中可下载(含下载地址) {{ drawingResult.downloadable }} 条; 调用地址 {{ testedAddress }}
                    </div>
                    <el-alert v-if="!drawingResult.total" type="warning" :closable="false" show-icon class="result-alert"
                              title="SAP未返回该物料的图纸信息"/>
                    <el-table v-else :data="drawingResult.drawings" size="small" border max-height="360">
                        <el-table-column type="index" label="#" width="45"/>
                        <el-table-column prop="type" label="文档类型" width="90"/>
                        <el-table-column prop="number" label="文档号" width="180"/>
                        <el-table-column prop="version" label="版本" width="70"/>
                        <el-table-column prop="part" label="部分" width="70"/>
                        <el-table-column prop="originFileName" label="文件名" min-width="180" show-overflow-tooltip/>
                        <el-table-column prop="application" label="后缀" width="80"/>
                        <el-table-column prop="description" label="描述" min-width="160" show-overflow-tooltip/>
                        <el-table-column prop="designer" label="责任人" width="110"/>
                        <el-table-column label="可下载" width="80">
                            <template slot-scope="scope">
                                <el-tag v-if="scope.row.hasUri" size="mini" type="success">是</el-tag>
                                <el-tag v-else size="mini" type="info">否</el-tag>
                            </template>
                        </el-table-column>
                    </el-table>
                </template>
            </template>
        </el-card>

        <el-card class="config-card cnf-test-card">
            <div slot="header">
                <span class="card-title">调用测试(CNF工序确认)</span>
                <el-tag size="small" type="danger">真实回传SAP</el-tag>
            </div>
            <!-- 回传不可撤销, 两个输入框有意不绑回车: 确认框打开后焦点在确认按钮上,
                 连续回车会"开框-确认-开框-确认"地把二次确认直接跑过去, 只留鼠标点击这一条触发路径 -->
            <div class="d-flex align-items-center">
                <el-input v-model="cnfOrderCode" placeholder="工单号" clearable style="width: 220px"
                          :disabled="cnfTesting"/>
                <el-input v-model="cnfOperationSeq" placeholder="工序号" clearable class="ml-2" style="width: 140px"
                          :disabled="cnfTesting"/>
                <b-button variant="danger" size="sm" class="ml-2" :disabled="cnfTesting || !cnfInputReady"
                          @click="testCnf">
                    <b-spinner v-if="cnfTesting" small class="mr-1"/>
                    <i v-else class="fa fa-fw fa-paper-plane mr-1"/>调用测试
                </b-button>
            </div>
            <div class="tip-text">
                按工单号+工序号执行与业务完全相同的整段CNF回传(取工序属性 → 写FIN_CONF=X → 提交SAP事务);
                该调用不受SAP接口调用开关拦截。
                <span class="danger-text">成功后SAP中会真实产生一条工序确认, 无法从本系统撤销, 请只用测试工单。</span>
            </div>

            <template v-if="cnfResult">
                <el-alert v-if="!cnfResult.success" type="error" :closable="false" show-icon class="result-alert"
                          :title="'调用失败: ' + (cnfResult.message || '未知错误')"/>
                <template v-else>
                    <el-alert type="success" :closable="false" show-icon class="result-alert"
                              :title="'工单 ' + cnfResult.orderCode + ' 工序 ' + cnfResult.operationSeq + ' CNF回传成功'"/>
                    <el-table v-if="cnfConfirmationRows.length" :data="cnfConfirmationRows" size="small" border
                              class="mt-2">
                        <el-table-column prop="field" label="SAP返回字段" width="160"/>
                        <el-table-column prop="value" label="值" show-overflow-tooltip/>
                    </el-table>
                </template>
            </template>
        </el-card>
    </div>
</template>

<script>
export default {
    name: "SithMesSapAddressConfig",
    data() {
        return {
            saving: false,
            loading: false,
            callAvailable: true,
            callSwitching: false,
            writeBackCalendarClosedNow: false,
            parseError: false,
            configJson: "",
            defaultConfig: {},
            configs: [],
            loadedJson: "[]",
            testMaterialCode: "",
            drawingTesting: false,
            drawingResult: null,
            cnfOrderCode: "",
            cnfOperationSeq: "",
            cnfTesting: false,
            cnfResult: null
        };
    },
    computed: {
        baseUrl() {
            return process.env.VUE_APP_SITHMES_MST_URL + "/sapAddressConfig";
        },
        activeRowIndex() {
            return this.configs.findIndex(row => row.active);
        },
        cleanedConfigs() {
            return this.configs
                .map(row => {
                    const item = {};
                    ["ashost", "sysnr", "client", "effectiveFrom"].forEach(key => {
                        if (row[key]) {
                            item[key] = row[key];
                        }
                    });
                    return item;
                })
                .filter(item => Object.keys(item).length > 0);
        },
        formJsonPreview() {
            return JSON.stringify(this.cleanedConfigs);
        },
        dirty() {
            return this.formJsonPreview !== this.loadedJson;
        },
        // 未配置地址覆盖时走的是sap-connect自身的默认地址, 与本页回显的默认值未必相同, 不能拿默认值顶上
        testedAddress() {
            const result = this.drawingResult;
            if (!result || !(result.ashost || result.sysnr || result.client)) {
                return "未配置地址覆盖, 使用SAP连接服务默认地址";
            }
            return [result.ashost, result.sysnr, result.client].map(item => item || "默认").join(" / ");
        },
        cnfInputReady() {
            return !!this.cnfOrderCode.trim() && !!this.cnfOperationSeq.trim();
        },
        cnfConfirmationRows() {
            const confirmation = (this.cnfResult && this.cnfResult.confirmation) || {};
            return Object.keys(confirmation)
                .filter(field => confirmation[field])
                .map(field => ({field, value: confirmation[field]}));
        }
    },
    created() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            this.$store.dispatch("api/get", {url: this.baseUrl + "/get"}).then(data => {
                this.loading = false;
                this.callAvailable = data.callAvailable;
                this.writeBackCalendarClosedNow = data.writeBackCalendarClosedNow;
                this.parseError = data.parseError;
                this.configJson = data.configJson || "";
                this.defaultConfig = data.defaultConfig || {};
                this.configs = (data.configs || []).map((config, index) => ({
                    ashost: config.ashost || "",
                    sysnr: config.sysnr || "",
                    client: config.client || "",
                    effectiveFrom: config.effectiveFrom || "",
                    saved: true,
                    active: index === data.activeIndex,
                    testing: false
                }));
                // 以加载后的表格内容为基准, 用于判断是否存在未保存的改动
                this.loadedJson = this.formJsonPreview;
            }).catch(() => {
                this.loading = false;
            });
        },
        onCallAvailableChange(value) {
            const tip = value
                ? "确认恢复SAP调用? 所有SAP接口立即放行"
                : "确认停用SAP调用? 除工时回写(CNF/PCNF)外的SAP接口将立即被拦截";
            this.$confirm(tip, "提示", {type: "warning"}).then(() => {
                this.callSwitching = true;
                this.$store.dispatch("api/post", {
                    url: this.baseUrl + "/setCallAvailable?available=" + (value ? 1 : 0)
                }).then(() => {
                    this.callSwitching = false;
                    this.$message.success(value ? "SAP调用已恢复" : "SAP调用已停用");
                    this.load();
                }).catch(() => {
                    // 失败时把开关拨回原位, 避免页面显示与实际参数不一致
                    this.callSwitching = false;
                    this.callAvailable = !value;
                });
            }).catch(() => {
                this.callAvailable = !value;
            });
        },
        refresh() {
            if (!this.dirty) {
                this.load();
                return;
            }
            this.$confirm("确认刷新? 当前未保存的改动(新增/修改/删除的配置行)将全部丢弃", "提示", {type: "warning"})
                .then(() => {
                    this.load();
                }).catch(() => {
                });
        },
        isFuture(row) {
            return row.effectiveFrom && new Date(row.effectiveFrom.replace(/-/g, "/")) > new Date();
        },
        isLocked(row) {
            return row.saved && !this.isFuture(row);
        },
        addRow() {
            this.configs.push({
                ashost: "", sysnr: "", client: "", effectiveFrom: "",
                saved: false, active: false, testing: false
            });
        },
        testConnection(row) {
            row.testing = true;
            this.$store.dispatch("api/post", {
                url: this.baseUrl + "/testConnection",
                data: {ashost: row.ashost, sysnr: row.sysnr, client: row.client}
            }).then(response => {
                row.testing = false;
                const result = response.data || {};
                const address = result.ashost + " / " + result.sysnr + " / " + result.client;
                if (result.success) {
                    this.$message.success("连接成功: " + address);
                } else {
                    this.$message({
                        type: "error",
                        message: "连接失败: " + address + " — " + (result.message || "未知错误"),
                        duration: 6000,
                        showClose: true
                    });
                }
            }).catch(() => {
                row.testing = false;
            });
        },
        testDrawingList() {
            const materialCode = this.testMaterialCode.trim();
            if (!materialCode || this.drawingTesting) {
                return;
            }
            this.drawingTesting = true;
            // 上一次结果先清掉: SAP调用可能耗时较久, 留着旧清单会被当成本次结果
            this.drawingResult = null;
            this.$store.dispatch("api/post", {
                url: this.baseUrl + "/testDrawingList",
                data: {materialCode}
            }).then(response => {
                this.drawingTesting = false;
                this.drawingResult = response.data || {};
                if (this.drawingResult.success) {
                    this.$message.success("调用成功, 返回" + this.drawingResult.total + "条图纸信息");
                } else {
                    // 结果区在长页面下方, 失败只靠那条alert容易被漏看
                    this.$message({
                        type: "error",
                        message: "调用失败: " + (this.drawingResult.message || "未知错误"),
                        duration: 6000,
                        showClose: true
                    });
                }
            }).catch(() => {
                this.drawingTesting = false;
            });
        },
        testCnf() {
            // 在途时直接挡掉: 这一步在SAP产生的确认撤不回来, 重复触发没有补救手段
            if (!this.cnfInputReady || this.cnfTesting) {
                return;
            }
            const orderCode = this.cnfOrderCode.trim();
            const operationSeq = this.cnfOperationSeq.trim();
            // CNF是真实回传, 成功后SAP里的确认无法从本系统撤销, 误点一次没有退路, 因此必须二次确认
            this.$confirm("确认对工单 " + orderCode + " 工序 " + operationSeq
                + " 执行CNF回传? 该操作会在SAP真实产生一条工序确认, 无法从本系统撤销", "提示", {type: "warning"})
                .then(() => {
                    this.cnfTesting = true;
                    this.cnfResult = null;
                    this.$store.dispatch("api/post", {
                        url: this.baseUrl + "/testCnf",
                        data: {orderCode, operationSeq}
                    }).then(response => {
                        this.cnfTesting = false;
                        this.cnfResult = response.data || {};
                        if (this.cnfResult.success) {
                            this.$message.success("CNF回传成功");
                        } else {
                            this.$message({
                                type: "error",
                                message: "CNF回传失败: " + (this.cnfResult.message || "未知错误"),
                                duration: 6000,
                                showClose: true
                            });
                        }
                    }).catch(() => {
                        this.cnfTesting = false;
                    });
                }).catch(() => {
                });
        },
        saveConfig() {
            this.$confirm("确认保存SAP地址配置? 已到生效时间的最新一条将立即生效", "提示", {type: "warning"})
                .then(() => {
                    this.saving = true;
                    this.$store.dispatch("api/post", {
                        url: this.baseUrl + "/save",
                        data: this.cleanedConfigs
                    }).then(() => {
                        this.saving = false;
                        this.$message.success("保存成功");
                        this.load();
                    }).catch(() => {
                        this.saving = false;
                    });
                }).catch(() => {
                });
        },
        clearConfig() {
            this.$confirm("确认清除全部地址配置? SAP调用将恢复使用默认地址", "提示", {type: "warning"})
                .then(() => {
                    this.$store.dispatch("api/post", {
                        url: this.baseUrl + "/save",
                        data: []
                    }).then(() => {
                        this.$message.success("已清除, 恢复默认地址");
                        this.load();
                    }).catch(() => {
                    });
                }).catch(() => {
                });
        }
    }
};
</script>

<style scoped lang="scss">
.sap-address-config {
    padding: 16px;

    .config-card {
        margin-bottom: 16px;
        max-width: 720px;

        &.address-card {
            max-width: 1080px;
        }

        &.drawing-test-card {
            max-width: 1280px;
        }

        &.cnf-test-card {
            max-width: 720px;
        }

        .card-title {
            font-weight: bold;
            margin-right: 8px;
        }
    }

    .parse-error-alert {
        margin-bottom: 8px;
    }

    .result-alert {
        margin-top: 12px;
    }

    .result-summary {
        margin: 12px 0 8px;
        font-size: 13px;
        color: #606266;
    }

    .table-actions {
        margin: 8px 0 16px;
    }

    // 表格内的 b-button(variant="link") 去掉 bootstrap 默认内边距, 保持原文字按钮的紧凑排布
    .cell-link {
        padding: 0 4px;
        vertical-align: baseline;
    }

    .danger-text {
        color: #f56c6c;
    }

    .tip-text {
        color: #909399;
        font-size: 12px;
        line-height: 1.6;
        margin-top: 4px;
    }
}
</style>
